import ActionMenu from "../../actionMenus/action-menu";
import dropdownIcon from "../../../assets/componentsIcons/dropdown/dropdown.svg";
import editIcon from "../../../assets/componentsIcons/dropdown/edit.svg";
import deleteIcon from "../../../assets/componentsIcons/dropdown/delete.svg";
import CalenderIcon from "../../../assets/svgs/calenderIcon";
import { LeaseApplicationService } from "@propertelligent/client-api";
import { useMemo, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import homeIconBlue from "../../../assets/homeIconBlue.svg";
import VerificationProgress from "../listing-progressbar/listing-progressbar";
import LeaseIcon from "../../../assets/svgs/unitIcons/leaseIcon";
import { useNavigate } from "react-router";
import apiHelper from "../../../utils/apiHelper";
import { ConfirmationPopup } from "../../confirmation-popup/confirmation-popup-demo";
import UtilHelper from "../../../utils/UtilHelper";
import { GetImages } from "../../../utils/GetImages";
import { URLS } from "../../../constants/constants";
import { usePermissions } from "../../../hooks/usePermissions";
import { PubSub, TopicsEnum } from "../../../utils/pubsub";

interface DataHookState {
  rows: any;
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  currentPage: number;
}

interface ProspectTenantProps {
  Id?: string;
  className?: string;
  IsFooter?: boolean;
  dataHookState?: DataHookState;
  hasAnyTenants?: boolean;
  isLink?: boolean;
}

export const footerData = [
  {
    iconSrc: <LeaseIcon className="stat-icon" />,
    label: "Lease Application",
    value: "Submitted",
    valueKey: "LeaseApplicationStatusTypeId",
  },
  {
    iconSrc: <CalenderIcon className="stat-icon" />,
    label: "Lease Agreement",
    value: "Payment Pending",
    valueKey: "LeaseAgreementStatusTypeId",
  },
  {
    iconSrc: <CalenderIcon className="stat-icon" />,
    label: "Move In",
    value: "NA",
    valueKey: "moveInStatus",
  },
];

const ProspectTenantCard: React.FC<ProspectTenantProps> = ({
  Id,
  className = "",
  IsFooter = true,
  dataHookState,
  hasAnyTenants = false,
  isLink = true,
}) => {
  const { rows, loading, hasMore, fetchData, currentPage } = dataHookState;

  const [deleteState, setDeleteState] = useState({
    id: null as number | null,
    isOpen: false,
  });

  const navigate = useNavigate();
  const { canWrite } = usePermissions();

  const MENU_ITEMS = useMemo(() => {
    const items = [];

    if (canWrite("Tenant")) {
      items.push({
        icon: editIcon,
        label: "Edit",
        customaction: (listingData: any) =>
          navigate("/tenants/edit/" + listingData.tenantId, {
            state: { from: window.location.pathname, activeTab: "Tenants" },
          }),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
      items.push({
        icon: deleteIcon,
        label: "Delete",
        action: (Id: number) => setDeleteState({ id: Id, isOpen: true }),
        isDivider: false,
        isDestructive: false,
      });
      items.push({ isDivider: true });
    }

    // Remove any trailing dividers
    while (items.length > 0 && items[items.length - 1].isDivider) {
      items.pop();
    }

    return items;
  }, [canWrite, navigate]);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  const handleDeleteTanent = async () => {
    if (!deleteState.id) return;
    try {
      const response = await apiHelper(
        LeaseApplicationService.deleteApiLeaseApplication,
        {
          showNotification: true,
          successMessage: "Tenant Deleted Successfully",
        },
        deleteState.id,
      );

      if (response?.data?.success) {
        setDeleteState({ id: null, isOpen: false });

        // 1. Notify the rest of the app that data has changed
        PubSub.publish(TopicsEnum.RefreshLeaseData.toString(), null);

        // 2. Redirect back to listing
        navigate(URLS.TENANTS);
      }
    } catch (err) {
      setDeleteState({ id: null, isOpen: false });
      throw err;
    } finally {
      setDeleteState({ id: null, isOpen: false });
    }
  };

  return (
    <div className="infinite-scroll-container">
      {rows.length === 0 && !loading ? (
        <EmptyState
          icon={GetImages.emptyStateTenant}
          heading={hasAnyTenants ? "No Tenants Found" : "Add Your First Tenant"}
          isButton={hasAnyTenants ? false : canWrite("Tenant")}
          buttonLabel={hasAnyTenants ? "Add Tenant" : "Add Tenant"}
          onButtonClick={() => navigate("/tenants/create")}
        />
      ) : (
        <>
          {rows?.map((listingData, index) => (
            <div
              key={index}
              id={Id}
              className={`tenant-card prospect-tenant-card-container ${className}`}
            >
              <div
                onClick={() =>
                  isLink &&
                  navigate(`/tenants/tenant-onboard/${listingData?.id}`)
                }
                className="tenant-card-header"
                style={{ cursor: isLink ? "pointer" : "auto" }}
              >
                <div className="tenant-avatar">
                  <div className="tenant-initials">
                    {UtilHelper.getInitials(
                      listingData?.firstName + " " + listingData?.lastName,
                    )}
                  </div>
                </div>
                <div className="tenant-details">
                  <h3 className="tenant-name">
                    {listingData?.firstName + " " + listingData?.lastName}
                  </h3>
                  <p className="tenant-address">{listingData?.streetAddress}</p>
                  <p className="tenant-contact">
                    {listingData?.email}
                    <span className="contact-dot"> • </span>
                    <br className="contact-break" />
                    {listingData?.formatted?.PhoneNumber}
                  </p>
                </div>

                <div className="onboarding-listing-progress">
                  <div className="listing-progress-wrapper">
                    <VerificationProgress
                      progress={listingData?.percentCompleted || 0}
                      progressText="Onboarding Progress"
                      progressStatus={
                        listingData?.formatted?.LeaseApplicationStatusTypeId ||
                        ""
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="separator-horizontal" />
              <div className="listing-card-footer">
                <div className="listing-stats">
                  {IsFooter && (
                    <>
                      {footerData?.map((stat, index) => {
                        const dynamicValue =
                          listingData?.formatted?.[stat.valueKey] ??
                          listingData?.[stat.valueKey] ??
                          "NA";
                        return (
                          <div className="stat" key={index}>
                            {stat.iconSrc}
                            <span className="stat-text">
                              {stat.label}: {dynamicValue}
                            </span>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>

                <div className="footer-actions">
                  {MENU_ITEMS.length > 0 && (
                    <ActionMenu
                      menuItems={MENU_ITEMS}
                      Id={listingData?.id}
                      entityId={listingData}
                      trigger={
                        <button className="action-button">
                          <img
                            src={dropdownIcon}
                            alt="More options"
                            width={20}
                            height={20}
                            className="action-icon"
                          />
                        </button>
                      }
                    />
                  )}
                </div>
              </div>
            </div>
          ))}

          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner
                  myClassName={rows?.length === 0 ? "Icon50" : "Icon40"}
                />
              )}
            </div>
          )}
        </>
      )}

      <ConfirmationPopup
        isOpen={deleteState.isOpen}
        title="Delete Tenant"
        message="Are you sure you want to delete this Tenant?"
        primaryButtonLabel="Delete"
        secondaryButtonLabel="Cancel"
        onPrimaryAction={handleDeleteTanent}
        onSecondaryAction={() => setDeleteState({ id: null, isOpen: false })}
        onClose={() => setDeleteState({ id: null, isOpen: false })}
        isPrimaryDestructive={true}
      />
    </div>
  );
};

export default ProspectTenantCard;
